import React, { FunctionComponent, useEffect, useMemo, useState } from "react"
import { Route, useHistory, useRouteMatch } from "react-router-dom"

import Loader from "../../../components/Loader/Loader"
import Seo from "../../../components/Seo/Seo"
import { notificationOptions } from "../../../api/Definitions"
import { iResponse, iUrlParams } from "../../../definitions/interfaces"
import { iSpace } from "../../../definitions/Space"

import s from "../Spaces.less"
import { FormProvider, useForm } from "react-hook-form"
import { ItemWithAccess } from "../../../definitions/types"
import { iAccess } from "../../../definitions/Access"
import {
  get,
  handleDeleteData,
  handleUpdateData,
} from "../../../store/data/actions"
import { unstable_batchedUpdates } from "react-dom"
import Header from "../Header"
import { store } from "react-notifications-component"
import SpaceOverview from "./Overview"
import SpaceUploads from "./Uploads"
import SpaceAbout from "./About"
import SpaceAccess from "./Access"

interface iProps {
  item: iSpace
  access: iAccess
  reloadSpace: () => void
}

const SpaceInspector: FunctionComponent<iProps> = ({
  item,
  access,
  reloadSpace,
}) => {
  const [isLoading, setLoading] = useState(false)
  const match = useRouteMatch<iUrlParams>()
  const history = useHistory()

  const methods = useForm<ItemWithAccess<iSpace>>({
    defaultValues: { ...item, __access: access },
    shouldUnregister: false,
  })

  const {
    formState: { dirtyFields },
  } = methods

  useEffect(() => {
    methods.reset({ ...item, __access: access })
  }, [item])

  const onSubmit = (data: ItemWithAccess<iSpace>) => {
    setLoading(true)
    handleUpdateData<ItemWithAccess<iSpace>>({
      data,
      fields: dirtyFields as Partial<ItemWithAccess<iSpace>>,
      link: "/v1/space",
    })
      .then(() => {
        reloadSpace()
        setLoading(false)
        store.addNotification({
          title: "Success!",
          message: "Space successfully updated!",
          // message: "teodosii@react-notifications-component",
          type: "success",
          ...notificationOptions,
        })
      })
      .catch((e) => {
        if (e) {
          if (e.reason === "Nothing changed!") {
            store.addNotification({
              message: e.reason,
              type: "warning",
              ...notificationOptions,
            })
          } else {
            store.addNotification({
              message: e.reason,
              type: "danger",
              ...notificationOptions,
            })
          }
        } else {
          store.addNotification({
            message: "Something went wrong!",
            type: "danger",
            ...notificationOptions,
          })
        }
        setLoading(false)
      })
  }

  const onDelete = () => {
    setLoading(true)
    if (confirm("Are you sure you want to delete this?")) {
      handleDeleteData({
        link: "/v1/spaces/" + item._id,
      })
        .then(() => {
          setLoading(false)
          store.addNotification({
            title: "Success!",
            message: "Space successfully deleted!",
            // message: "teodosii@react-notifications-component",
            type: "success",
            ...notificationOptions,
          })
          history.goBack()
        })
        .catch((e) => {
          if (e) {
            if (e.reason === "Nothing changed!") {
              store.addNotification({
                message: e.reason,
                type: "warning",
                ...notificationOptions,
              })
            } else {
              store.addNotification({
                message: e.reason,
                type: "danger",
                ...notificationOptions,
              })
            }
          } else {
            store.addNotification({
              message: "Something went wrong!",
              type: "danger",
              ...notificationOptions,
            })
          }
          setLoading(false)
        })
    }
  }

  const invokeSubmit = () => {
    methods.handleSubmit((data) => {
      onSubmit(data as ItemWithAccess<iSpace>)
    })()
  }

  return (
    <FormProvider {...methods}>
      <Seo title={"Space Inspector"} />
      <Header
        access={access}
        title={item.title}
        cover={item.previewImageId}
        submitDeleteText={"Delete"}
        submitText="Update"
        handleSubmit={invokeSubmit}
        handleDelete={onDelete}
      />
      <div className={`inspector ${s.inspector}`}>
        {isLoading ? (
          <Loader />
        ) : (
          <form
            onSubmit={methods.handleSubmit((data) => {
              onSubmit(data as ItemWithAccess<iSpace>)
            })}
            className="inspector-form"
          >
            <Route exact path={match.path}>
              <SpaceOverview space={item} />
            </Route>
            <Route exact path={match.path + "/about"}>
              <SpaceAbout />
            </Route>
            <Route exact path={match.path + "/manage-files"}>
              <SpaceUploads item={item} />
            </Route>
            <Route exact path={match.path + "/access"}>
              <SpaceAccess access={access} />
            </Route>
            <button className="d-none" type="submit">
              Submit
            </button>
          </form>
        )}
      </div>
    </FormProvider>
  )
}

const SpaceLoader = () => {
  const [isLoading, setLoading] = useState(true)
  const [access, setAccess] = useState<iAccess>()
  const [item, setItem] = useState<iSpace>()
  const match = useRouteMatch<iUrlParams>()
  const history = useHistory()
  const loadSpace = () => {
    Promise.all([
      get<iResponse<iAccess>>({
        link: "/v1/access",
        query: { url: match.params.url, collection: "Spaces" },
      }),
      get<iResponse<iSpace>>({
        link: "/v2/spaces/" + match.params.url,
      }),
    ])
      .then((result) => {
        const [accessResult, dataResult] = result
        unstable_batchedUpdates(() => {
          setItem(dataResult.data)
          setAccess(accessResult.data)
          setLoading(false)
        })
      })
      .catch((err) => {
        setLoading(false)
        if (err?.status === 403) {
          history.replace("/no-permission")
        }
      })
  }
  useMemo(() => {
    loadSpace()
  }, [])

  return isLoading ? (
    <Loader />
  ) : item && access ? (
    <SpaceInspector item={item} access={access} reloadSpace={loadSpace} />
  ) : null
}

export default SpaceLoader
